import React, { useState, useEffect, useRef } from "react";
import ReactDOM from "react-dom";
import { KpiIcon } from "../../KpiIcons";
import { apiCall } from "../../../../../../_services/apiCall";
import config from "../../../../../../config/config.json";
import { fmt, extractArray, isOk } from "./utils";
import Badge from "./Badge";
import HistoryEntryCard from "./HistoryEntryCard";

const HistoryModal = ({ answer, locs, questionId, onClose }) => {
  const [audit, setAudit] = useState([]);
  const [loading, setLoading] = useState(true);
  const overlayRef = useRef(null);

  useEffect(() => {
    const load = async () => {
      try {
        const r = await apiCall(
          `${config.POSTLOGIN_API_URL_COMPANY}quantative-data/${answer.id}/history`,
          {},
          {},
          "GET"
        );
        if (isOk(r)) setAudit(extractArray(r));
      } catch {}
      setLoading(false);
    };
    load();
  }, [answer.id]);

  useEffect(() => {
    const h = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", h);
    return () => document.removeEventListener("keydown", h);
  }, [onClose]);

  const locId = answer.location_id || answer.locationId;
  const locObj = locs.find((l) => String(l.id) === String(locId));
  const fromDate = answer.from_date || answer.fromDate;
  const toDate = answer.to_date || answer.toDate;

  const handleOverlay = (e) => {
    if (e.target === overlayRef.current) onClose();
  };

  // Sort: newest first
  const sorted = [...audit].sort(
    (a, b) =>
      new Date(b.createdAt || b.created_at || 0) -
      new Date(a.createdAt || a.created_at || 0)
  );

  const modal = (
    <div className="ahp-overlay" ref={overlayRef} onClick={handleOverlay}>
      <div className="ahp-modal" style={{ maxWidth: 760 }}>
        {/* Header */}
        <div className="ahp-modal-hd">
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div
              style={{
                width: 34,
                height: 34,
                minWidth: 34,
                minHeight: 34,
                maxWidth: 34,
                maxHeight: 34,
                borderRadius: 9,
                background: "var(--blue3)",
                border: "1px solid var(--blue2)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
                overflow: "hidden",
              }}
            >
              <KpiIcon name="history" size={16} color="var(--blue)" />
            </div>
            <div>
              <div
                style={{
                  fontSize: 14,
                  fontWeight: 800,
                  color: "var(--ink)",
                  letterSpacing: "-.01em",
                }}
              >
                Answer History
              </div>
              <div
                style={{
                  fontSize: 11,
                  color: "var(--ink4)",
                  marginTop: 1,
                  display: "flex",
                  gap: 8,
                  alignItems: "center",
                  flexWrap: "wrap",
                }}
              >
                {fromDate && toDate && (
                  <span>
                    {fmt(fromDate, { day: "2-digit", month: "short" })} →{" "}
                    {fmt(toDate, { day: "2-digit", month: "short" })}
                  </span>
                )}
                {locObj && <span>· {locObj.name}</span>}
                {answer.value != null && (
                  <span style={{ fontWeight: 700, color: "var(--ink2)" }}>
                    · {answer.value} {answer.unit || ""}
                  </span>
                )}
                <Badge status={answer.status} />
                {!loading && audit.length > 0 && (
                  <span
                    style={{
                      background: "var(--blue3)",
                      color: "var(--blue)",
                      padding: "1px 7px",
                      borderRadius: 20,
                      fontSize: 9.5,
                      fontWeight: 700,
                      fontFamily: "var(--fm)",
                    }}
                  >
                    {audit.length} entries
                  </span>
                )}
              </div>
            </div>
          </div>
          <button
            className="ahp-close-btn"
            onClick={onClose}
            style={{
              width: 30,
              height: 30,
              borderRadius: "50%",
              border: "1.5px solid var(--bdr)",
              background: "transparent",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
              transition: "background .12s",
            }}
          >
            <KpiIcon name="x" size={13} color="var(--ink3)" />
          </button>
        </div>

        {/* Body */}
        <div className="ahp-modal-bd">
          {loading ? (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                padding: "32px 0",
                color: "var(--ink4)",
                fontSize: 12,
                justifyContent: "center",
              }}
            >
              <span
                className="ahp-spin"
                style={{
                  width: 14,
                  height: 14,
                  border: "2px solid var(--bdr)",
                  borderTopColor: "var(--blue)",
                }}
              />
              Loading history…
            </div>
          ) : !sorted.length ? (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                padding: "44px 0",
                gap: 10,
                textAlign: "center",
              }}
            >
              <div
                style={{
                  width: 40,
                  height: 40,
                  minWidth: 40,
                  minHeight: 40,
                  maxWidth: 40,
                  maxHeight: 40,
                  borderRadius: 12,
                  background: "var(--bdr2)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                  overflow: "hidden",
                }}
              >
                <KpiIcon name="history" size={18} color="var(--ink4)" />
              </div>
              <div
                style={{ fontWeight: 700, fontSize: 13, color: "var(--ink2)" }}
              >
                No history yet
              </div>
              <div
                style={{
                  fontSize: 11.5,
                  color: "var(--ink4)",
                  maxWidth: 240,
                }}
              >
                Changes to this answer will appear here as a timeline.
              </div>
            </div>
          ) : (
            <div>
              {/* Legend */}
              <div
                style={{
                  display: "flex",
                  gap: 14,
                  marginBottom: 18,
                  paddingBottom: 14,
                  borderBottom: "1px solid var(--bdr2)",
                  flexWrap: "wrap",
                }}
              >
                {[
                  { color: "var(--green)", bg: "#bbf7d0", label: "Created" },
                  { color: "var(--blue)", bg: "var(--blue2)", label: "Updated" },
                ].map(({ color, bg, label }) => (
                  <div
                    key={label}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 6,
                      fontSize: 11,
                      color: "var(--ink3)",
                      fontWeight: 600,
                    }}
                  >
                    <div
                      style={{
                        width: 8,
                        height: 8,
                        borderRadius: "50%",
                        background: color,
                        border: `2px solid ${bg}`,
                        flexShrink: 0,
                      }}
                    />
                    {label}
                  </div>
                ))}
                <span
                  style={{
                    marginLeft: "auto",
                    fontSize: 10.5,
                    color: "var(--ink4)",
                    fontFamily: "var(--fm)",
                  }}
                >
                  Newest first · {sorted.length} total
                </span>
              </div>

              {/* Timeline entries */}
              {sorted.map((entry, i) => (
                <HistoryEntryCard
                  key={entry.id || i}
                  entry={entry}
                  locs={locs}
                  isLast={i === sorted.length - 1}
                  index={i}
                />
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div
          style={{
            padding: "14px 24px",
            borderTop: "1px solid var(--bdr2)",
            background: "#fafbfc",
            borderRadius: "0 0 16px 16px",
            display: "flex",
            justifyContent: "flex-end",
          }}
        >
          <button className="ahp-btn ahp-btn-g" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );

  // Render via portal so the fixed overlay always centres relative to the
  // viewport regardless of which parent has CSS transforms or stacking contexts.
  return ReactDOM.createPortal(modal, document.body);
};

export default HistoryModal;
